/**
 * Yelp Transformers — wire-format → flat lowerCamelCase records
 * (Story 23.5 AC #3, #4, #11)
 *
 * Yelp Fusion's wire format is snake_case (`review_count`, `image_url`,
 * `is_closed`, etc.). The transformers emit lowerCamelCase keys
 * (`reviewCount`, `imageUrl`, `isClosed`) so widget config matches the
 * project-wide naming convention.
 *
 * **CSV-injection sanitization** is applied to every string-valued field
 * destined for export via `stripCsvInjectionPrefix` (re-exported from
 * CallRail's `pii.ts`).
 *
 * The build-time test in `transformers.test.ts` asserts no Yelp record
 * key ever uses snake_case (e.g., `record.review_count` is undefined,
 * `record.reviewCount` is defined).
 */
import { stripCsvInjectionPrefix } from "./pii";
import type {
  YelpBusinessDetails,
  YelpReview,
  YelpReviewsResponse,
} from "./api";
import type { YelpRatingSnapshotRow } from "./snapshots";

/**
 * `_reportType` discriminator literals — every record carries exactly
 * one. Mirrors Trustpilot 23.3 + Google Reviews 23.1 `_reportType` shape.
 */
export type YelpReportType =
  | "businessDetails"
  | "reviewAggregation"
  | "reviewExcerpt"
  | "ratingSnapshot";

/**
 * Compute a sentiment bucket from a star rating (1-5 scale) per AC #3:
 *  - 4-5 stars → `"positive"`
 *  - 3 stars   → `"neutral"`
 *  - 1-2 stars → `"negative"`
 *
 * Identical rule to Google Reviews 23.1 + Trustpilot 23.3.
 */
export function yelpSentimentBucket(
  stars: number,
): "positive" | "neutral" | "negative" {
  if (stars >= 4) return "positive";
  if (stars === 3) return "neutral";
  return "negative";
}

function safeString(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "string") return null;
  return stripCsvInjectionPrefix(v);
}

function safeNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== "number") return null;
  // Round 2 review fix (Blind Hunter): NaN / Infinity must not propagate to
  // records (e.g., coordinates.latitude). Coerce to null instead so the
  // downstream widget/log can render a clean placeholder.
  if (!Number.isFinite(v)) return null;
  return v;
}

function safeBoolean(v: unknown, fallback: boolean | null = null): boolean | null {
  if (v === null || v === undefined) return fallback;
  if (typeof v !== "boolean") return fallback;
  return v;
}

/**
 * Transform a Yelp Business Details API response into the flat marquee KPI
 * row (`_reportType: "businessDetails"`). Emits ONE record per fetch.
 */
export function transformYelpBusinessDetails(
  raw: YelpBusinessDetails,
): Record<string, unknown> {
  const location = raw.location ?? {};
  const categories = (raw.categories ?? []).map((c) => ({
    alias: safeString(c.alias),
    title: safeString(c.title),
  }));
  return {
    _reportType: "businessDetails" as YelpReportType,
    businessId: raw.id,
    alias: safeString(raw.alias),
    businessName: safeString(raw.name),
    primaryImageUrl: safeString(raw.image_url),
    isClosed: safeBoolean(raw.is_closed, false),
    isClaimed: safeBoolean(raw.is_claimed, null),
    yelpBusinessUrl: safeString(raw.url),
    // Round 2 review fix (Blind Hunter): fall back to null (NOT 0) so shape
    // drift cannot silently look like a legitimate zero-review business in
    // the records[] array. The upstream api.ts shape gate already rejects
    // non-finite numerics, but this defensive duplication ensures any
    // future caller bypassing the helper still surfaces the drift clearly.
    reviewCount:
      typeof raw.review_count === "number" &&
      Number.isFinite(raw.review_count as number)
        ? raw.review_count
        : null,
    rating:
      typeof raw.rating === "number" && Number.isFinite(raw.rating as number)
        ? raw.rating
        : null,
    categories,
    transactionsSupported: (raw.transactions ?? []).map((t) => safeString(t)),
    priceTier: safeString(raw.price ?? null),
    phone: safeString(raw.phone ?? null),
    displayPhone: safeString(raw.display_phone ?? null),
    photosArray: (raw.photos ?? []).map((p) => safeString(p)),
    locationCity: safeString(location.city ?? null),
    locationState: safeString(location.state ?? null),
    locationZipCode: safeString(location.zip_code ?? null),
    locationCountry: safeString(location.country ?? null),
    locationAddress1: safeString(location.address1 ?? null),
    locationAddress2: safeString(location.address2 ?? null),
    locationAddress3: safeString(location.address3 ?? null),
    displayAddress: (location.display_address ?? []).map((a) => safeString(a)),
    coordinates: raw.coordinates
      ? {
          latitude: safeNumber(raw.coordinates.latitude),
          longitude: safeNumber(raw.coordinates.longitude),
        }
      : null,
  };
}

/**
 * Transform a single Yelp review into a per-excerpt record
 * (`_reportType: "reviewExcerpt"`). Emits ONE record per excerpt
 * fetched (max 3 — Story 23.5 clamps `limit` to 3).
 */
export function transformYelpReview(
  raw: YelpReview,
  businessId: string,
): Record<string, unknown> {
  // Round 3 review fix (Blind Hunter HIGH): route rating through `safeNumber`
  // so NaN/Infinity from shape-drift cannot silently classify as "negative"
  // sentiment (NaN >= 4 and NaN === 3 are both false → falls into negative).
  // The `sentimentBucket` is gated on a non-null finite rating; otherwise
  // emit `null` so downstream widgets render an "unknown" placeholder
  // instead of inventing a sentiment.
  const rating = safeNumber(raw.rating);
  return {
    _reportType: "reviewExcerpt" as YelpReportType,
    businessId,
    reviewId: raw.id,
    reviewRating: rating,
    userId: safeString(raw.user?.id ?? null),
    userName: safeString(raw.user?.name ?? null),
    userProfileUrl: safeString(raw.user?.profile_url ?? null),
    userImageUrl: safeString(raw.user?.image_url ?? null),
    reviewExcerptText: safeString(raw.text),
    reviewCreatedTime: safeString(raw.time_created),
    reviewUrl: safeString(raw.url),
    sentimentBucket: rating === null ? null : yelpSentimentBucket(rating),
    date: safeString(raw.time_created),
  };
}

/**
 * Story 23.6 — synthetic-aggregate metric: `ratingTrendDirection`.
 *
 * Computes the trend direction from a rating-snapshot window per the
 * `metric-allowlist.ts` derivation rule:
 *   - `1`  = improving (secondHalfMean > firstHalfMean + 0.1)
 *   - `0`  = flat (within ±0.1)
 *   - `-1` = declining (secondHalfMean < firstHalfMean - 0.1)
 *
 * Returns `null` when there are fewer than 6 snapshots — too small to
 * reliably split into halves — OR when ANY snapshot row has a non-
 * finite rating (defensive against D1 column corruption / shape drift).
 *
 * **Canonical splitting convention:** the input is sorted ASC by
 * `snapshotDate` (defensively — internal sort, see Round-2 review fix
 * vs. Edge Case Hunter #3). For an N-element array, the first half is
 * `[0, floor(N/2))` and the second half is `[N - floor(N/2), N)`. For
 * EVEN N the halves are equal-sized and CONTIGUOUS (every row is in
 * exactly one half — no pivot exclusion). For ODD N the halves are
 * equal-sized and the middle row at index `floor(N/2)` is excluded
 * from both halves. This convention is shared between the production
 * adapter, the fixture generator, and the catalogue metric description —
 * preventing drift (Round 1 review fix). Comparison uses an epsilon-
 * tolerant float compare to defend against IEEE-754 boundary cases
 * like `4.4 - 4.3 = 0.10000000000000053`.
 */
export const YELP_RATING_TREND_THRESHOLD = 0.1;
const YELP_RATING_TREND_EPSILON = 1e-9;

export function computeYelpRatingTrendDirection(
  snapshots: ReadonlyArray<YelpRatingSnapshotRow>,
): -1 | 0 | 1 | null {
  if (snapshots.length < 6) return null;
  // Round-2 review fix (Blind Hunter #4 + Edge Case Hunter #3) — refuse
  // any non-finite rating: a single NaN would silently produce a
  // false-positive "flat" verdict via NaN propagation through the means.
  for (const r of snapshots) {
    if (typeof r.rating !== "number" || !Number.isFinite(r.rating))
      return null;
  }
  // Round-2 review fix (Blind Hunter #4) — defensively sort ASC inside
  // the function. The parameter is no longer named `snapshotsAsc` because
  // the function enforces the ordering. Mutating the input would be a
  // surprise to the caller; clone first.
  const sorted = [...snapshots].sort((a, b) =>
    a.snapshotDate.localeCompare(b.snapshotDate),
  );
  const halfSize = Math.floor(sorted.length / 2);
  const firstHalf = sorted.slice(0, halfSize);
  const secondHalf = sorted.slice(sorted.length - halfSize);
  if (firstHalf.length === 0 || secondHalf.length === 0) return null;
  const firstMean =
    firstHalf.reduce((acc, r) => acc + r.rating, 0) / firstHalf.length;
  const secondMean =
    secondHalf.reduce((acc, r) => acc + r.rating, 0) / secondHalf.length;
  const diff = secondMean - firstMean;
  if (diff > YELP_RATING_TREND_THRESHOLD + YELP_RATING_TREND_EPSILON) return 1;
  if (diff < -(YELP_RATING_TREND_THRESHOLD + YELP_RATING_TREND_EPSILON))
    return -1;
  return 0;
}

/**
 * Story 23.6 — synthetic-aggregate metric: `reviewVelocityPerMonth`.
 *
 * Rolling 30-day review count = current reviewCount minus the snapshot
 * whose `snapshotDate` is closest to (today - 30 days). The reference
 * "today" is the LATEST snapshot's date in the input window. The
 * function refuses to compute when:
 *   - `currentReviewCount` is non-finite
 *   - input has < 31 snapshots (so the prior-30-days snapshot is
 *     guaranteed distinct from the newest)
 *   - the located prior snapshot's `snapshotDate` is more than 3 days
 *     away from the (latest - 30 days) target (handles gappy snapshot
 *     streams; rejects when the velocity reference would be skewed)
 *
 * On review-count decrement (rare — review deletion / spam removal),
 * the function clamps to 0 and emits a structured-log event so
 * operators can correlate downstream "0 velocity" tiles with the
 * decrement event.
 *
 * Round-2 review fix (Blind Hunter #3 + Edge Case Hunter #2) — replaces
 * the original `length - 30` index shortcut which (a) was off-by-one at
 * boundary, (b) didn't validate sort order, (c) didn't validate that
 * the prior snapshot was actually ~30 days old.
 */
export function computeYelpReviewVelocityPerMonth(
  snapshots: ReadonlyArray<YelpRatingSnapshotRow>,
  currentReviewCount: number | null,
): number | null {
  if (
    typeof currentReviewCount !== "number" ||
    !Number.isFinite(currentReviewCount)
  ) {
    return null;
  }
  if (snapshots.length < 31) return null;
  // Defensive ASC sort + reference-date lookup (Round-2 review fix —
  // Blind Hunter #4 + Edge Case Hunter #2/#3).
  const sorted = [...snapshots].sort((a, b) =>
    a.snapshotDate.localeCompare(b.snapshotDate),
  );
  const newestSnapshot = sorted[sorted.length - 1];
  if (!newestSnapshot) return null;
  const newestMs = Date.parse(`${newestSnapshot.snapshotDate}T00:00:00.000Z`);
  if (!Number.isFinite(newestMs)) return null;
  const targetMs = newestMs - 30 * 24 * 60 * 60 * 1000;
  // Find the snapshot whose date is closest to (newest - 30 days).
  let best: YelpRatingSnapshotRow | null = null;
  let bestDeltaMs = Number.POSITIVE_INFINITY;
  for (const row of sorted) {
    const rowMs = Date.parse(`${row.snapshotDate}T00:00:00.000Z`);
    if (!Number.isFinite(rowMs)) continue;
    const delta = Math.abs(rowMs - targetMs);
    if (delta < bestDeltaMs) {
      bestDeltaMs = delta;
      best = row;
    }
  }
  if (best === null) return null;
  // Refuse if the closest-match is more than 3 days away from the
  // target — guards against gappy snapshot streams where the 30-day
  // reference would be skewed.
  const maxToleranceMs = 3 * 24 * 60 * 60 * 1000;
  if (bestDeltaMs > maxToleranceMs) return null;
  const delta = currentReviewCount - best.reviewCount;
  // Round-2 review fix (Edge Case Hunter #4) + Round-3 review fix
  // (Edge Case Hunter #1) — clamp negative deltas to 0 (review deletion /
  // spam removal). The helper stays PURE (no I/O); the adapter boundary
  // is responsible for emitting an observability event when a decrement
  // is detected. Operators correlate the empty velocity KPI with the
  // adapter-level log that has full ctx (tenantId / integrationId /
  // businessId) in scope.
  if (delta < 0) return 0;
  return delta;
}

/**
 * Story 23.6 — star-bucket aggregation over excerpt reviews. Counts the
 * integer rating (1-5) of each review and emits 5 bucket counts. Used
 * by preset #2 `yelp-rating-distribution-bar` and the spec AC #1
 * baseMetrics block. Non-finite or out-of-range ratings are dropped
 * (defensive — production shape-gate enforces numeric+finite upstream).
 */
function countYelpStarBuckets(
  reviews: ReadonlyArray<YelpReview>,
): {
  fiveStarCount: number;
  fourStarCount: number;
  threeStarCount: number;
  twoStarCount: number;
  oneStarCount: number;
} {
  const counts = {
    fiveStarCount: 0,
    fourStarCount: 0,
    threeStarCount: 0,
    twoStarCount: 0,
    oneStarCount: 0,
  };
  for (const r of reviews) {
    if (typeof r.rating !== "number" || !Number.isFinite(r.rating)) continue;
    const bucket = Math.round(r.rating);
    if (bucket === 5) counts.fiveStarCount++;
    else if (bucket === 4) counts.fourStarCount++;
    else if (bucket === 3) counts.threeStarCount++;
    else if (bucket === 2) counts.twoStarCount++;
    else if (bucket === 1) counts.oneStarCount++;
  }
  return counts;
}

/**
 * Build the review-aggregation row (`_reportType: "reviewAggregation"`).
 * Emitted EVEN WHEN `reviewsEndpointEntitled: false` per AC #3 — the
 * delta fields populate from snapshots; the excerpt-derived fields are
 * NULL on free tier.
 *
 * Story 23.6 extension: when `historicalSnapshots` is provided, the
 * row also carries the 5 star-bucket counts (preset #2) +
 * `ratingTrendDirection` + `reviewVelocityPerMonth` synthetic-aggregate
 * metrics (preset #8). Both halves use a shared canonical derivation
 * so fixture-mode and production-mode emit byte-identical aggregation
 * shapes.
 */
export function buildYelpReviewAggregation(args: {
  readonly businessId: string;
  readonly currentRating: number | null;
  readonly currentReviewCount: number | null;
  readonly priorSnapshot: YelpRatingSnapshotRow | null;
  readonly reviewsResponse: YelpReviewsResponse | null;
  readonly historicalSnapshots?: ReadonlyArray<YelpRatingSnapshotRow>;
}): Record<string, unknown> {
  const {
    businessId,
    currentRating,
    currentReviewCount,
    priorSnapshot,
    reviewsResponse,
    historicalSnapshots,
  } = args;

  // Round 3 review fix (Edge Case Hunter CRITICAL): defensive finite-guards
  // on the inputs. The api.ts shape gate already enforces numeric+finite,
  // but the type signature now accepts null to match `transformYelpBusinessDetails`
  // which falls back to null on shape drift. Avoid silent NaN propagation
  // into the delta math.
  const currentRatingFinite =
    typeof currentRating === "number" && Number.isFinite(currentRating);
  const currentReviewCountFinite =
    typeof currentReviewCount === "number" &&
    Number.isFinite(currentReviewCount);

  let avgExcerptRating: number | null = null;
  // Round 3 review fix (Blind Hunter Medium): emit `null` (not 0) for the
  // excerpt-count fields when there is no `reviewsResponse` so downstream
  // widgets can disambiguate "entitled but empty" from "not entitled".
  let excerptCount: number | null = null;
  let possibleLanguagesCount: number | null = null;
  let totalExcerptsReturned: number | null = null;
  // Story 23.6 — star-bucket aggregation over excerpts (preset #2).
  // Defaults to all-zero when there are no excerpts (free-tier or empty
  // response); preset #2 renderer will still render five empty bars +
  // the small-sample annotation.
  let starBuckets = {
    fiveStarCount: 0,
    fourStarCount: 0,
    threeStarCount: 0,
    twoStarCount: 0,
    oneStarCount: 0,
  };
  if (reviewsResponse) {
    excerptCount = reviewsResponse.reviews.length;
    possibleLanguagesCount = reviewsResponse.possible_languages?.length ?? 0;
    totalExcerptsReturned = reviewsResponse.total;
    if (reviewsResponse.reviews.length > 0) {
      const sum = reviewsResponse.reviews.reduce((acc, r) => {
        const v = typeof r.rating === "number" ? r.rating : 0;
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
      avgExcerptRating =
        Math.round((sum / reviewsResponse.reviews.length) * 100) / 100;
      starBuckets = countYelpStarBuckets(reviewsResponse.reviews);
    }
  }

  const ratingDelta =
    priorSnapshot !== null && currentRatingFinite
      ? Math.round(((currentRating as number) - priorSnapshot.rating) * 100) /
        100
      : null;
  const reviewCountDelta =
    priorSnapshot !== null && currentReviewCountFinite
      ? (currentReviewCount as number) - priorSnapshot.reviewCount
      : null;

  // Story 23.6 — synthetic-aggregate metrics. Computed from the
  // historicalSnapshots window when provided; otherwise null (the row
  // still emits all earlier fields so production-mode-without-snapshots
  // doesn't break — the preset #8 KPI tile renders an empty cell with
  // a "data not available" tooltip rather than crashing).
  const ratingTrendDirection = historicalSnapshots
    ? computeYelpRatingTrendDirection(historicalSnapshots)
    : null;
  const reviewVelocityPerMonth = historicalSnapshots
    ? computeYelpReviewVelocityPerMonth(historicalSnapshots, currentReviewCount)
    : null;

  return {
    _reportType: "reviewAggregation" as YelpReportType,
    businessId,
    avgExcerptRating,
    excerptCount,
    possibleLanguagesCount,
    totalExcerptsReturned,
    ratingDelta,
    reviewCountDelta,
    // Story 23.6 — star-bucket distribution (preset #2 data source).
    fiveStarCount: starBuckets.fiveStarCount,
    fourStarCount: starBuckets.fourStarCount,
    threeStarCount: starBuckets.threeStarCount,
    twoStarCount: starBuckets.twoStarCount,
    oneStarCount: starBuckets.oneStarCount,
    // Story 23.6 — synthetic-aggregate metrics (preset #8 4th-slot KPI).
    ratingTrendDirection,
    reviewVelocityPerMonth,
  };
}

/**
 * Transform a yelp_rating_snapshots row into a `_reportType: "ratingSnapshot"`
 * record. Emitted once per historical row in the requested dateRange.
 */
export function transformYelpRatingSnapshot(
  row: YelpRatingSnapshotRow,
): Record<string, unknown> {
  return {
    _reportType: "ratingSnapshot" as YelpReportType,
    businessId: row.businessId,
    snapshotDate: row.snapshotDate,
    rating: row.rating,
    reviewCount: row.reviewCount,
    date: row.snapshotDate,
  };
}
